import { create } from 'zustand';

/** what the user picked; 'system' follows the phone or computer (ADR-0028) */
export type Theme = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';
const KEY = 'ste.theme';
// background of the header area per theme (index.html theme-color)
const BAR_COLOR: Record<ResolvedTheme, string> = { dark: '#1b2220', light: '#e3e9e6' };

function stored(): Theme {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : undefined;

function resolve(theme: Theme): ResolvedTheme {
  if (theme !== 'system') return theme;
  return media?.matches ? 'dark' : 'light';
}

function apply(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('light', resolved === 'light');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR_COLOR[resolved]);
  for (const listener of listeners) listener(resolved);
}

/** native code (status bar) follows the theme */
const listeners = new Set<(resolved: ResolvedTheme) => void>();
export function onThemeChange(listener: (resolved: ResolvedTheme) => void): void {
  listeners.add(listener);
  listener(useTheme.getState().resolved);
}

interface ThemeState {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme(theme: Theme): void;
}

export const useTheme = create<ThemeState>((set, get) => {
  const initial = stored();
  const resolved = resolve(initial);
  apply(resolved);
  media?.addEventListener('change', () => {
    if (get().theme !== 'system') return;
    const next = resolve('system');
    apply(next);
    set({ resolved: next });
  });
  return {
    theme: initial,
    resolved,
    setTheme(theme) {
      const next = resolve(theme);
      apply(next);
      try {
        if (theme === 'system') localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, theme);
      } catch {
        // ignore
      }
      set({ theme, resolved: next });
    },
  };
});
